import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { liftHistoryQueryOptions, toLiftFieldsOf, toLiftSetSources } from '../api/liftHistoryQueries';
import type { LiftFields } from '../model/liftLines';
import { toLiftSessions, type LiftSession } from '../model/liftSessions';

export interface LiftHistoryState {
  readonly sessions: readonly LiftSession[];
  /** What the movement measures; `null` while unread or when the backend predates the field list. */
  readonly fields: LiftFields;
  /** True until EVERY page has arrived: a chart drawn from the first page is a partial answer. */
  readonly pending: boolean;
  readonly failed: boolean;
  readonly retry: () => void;
}

const NO_SESSIONS: readonly LiftSession[] = [];

export function useLiftHistory(clientId: number, exerciseId: number | null): LiftHistoryState {
  const history = useInfiniteQuery({
    ...liftHistoryQueryOptions(clientId, exerciseId ?? 0),
    enabled: exerciseId !== null,
  });
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = history;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const pages = history.data?.pages;
  return {
    sessions: pages === undefined ? NO_SESSIONS : toLiftSessions(toLiftSetSources(pages)),
    fields: pages === undefined ? null : toLiftFieldsOf(pages),
    pending:
      exerciseId === null ||
      history.isPending ||
      (history.isError && history.isFetching) ||
      (hasNextPage && !isError),
    failed: history.isError,
    retry: () => {
      void history.refetch();
    },
  };
}
